import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Building2, LayoutGrid, LineChart, Sparkles, Mail, Phone, MapPin } from 'lucide-react';
import ContactForm from '@/components/ContactForm';
import { useLangLink } from '@/lib/langRouter';

const IMG_HERO = 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=1600&q=80';
const IMG_SEC1 = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80';
const IMG_SEC2 = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80';
const IMG_PROJ1 = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1200&q=80';
const IMG_PROJ2 = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80';
const IMG_PROJ3 = 'https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=1200&q=80';

export default function Home() {
  const { t } = useTranslation();
  const l = useLangLink();

  return (
    <main className="pt-[72px]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-white border border-[#0B1E3F]/10 rounded-full px-4 py-1.5 text-[11.5px] uppercase tracking-[0.16em] text-[#0B1E3F]/70">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E9B949]" />
                {t('hero.eyebrow')}
              </div>
              <h1 className="mt-6 font-serif text-[42px] leading-[1.05] md:text-[68px] md:leading-[1.02] font-semibold tracking-[-0.02em] text-[#0B1E3F]">
                {t('hero.title')}<br />
                <span className="italic text-[#0B1E3F]/70">{t('hero.titleAccent')}</span>
              </h1>
              <p className="mt-6 max-w-[560px] text-[16px] md:text-[17px] leading-relaxed text-[#0B1E3F]/70">
                {t('hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#contact" className="inline-flex items-center gap-2 bg-[#0B1E3F] hover:bg-[#12295a] text-white text-[14px] font-semibold px-6 py-3.5 rounded-full transition" data-testid="hero-cta-primary">
                  {t('hero.ctaPrimary')} <ArrowUpRight size={16} />
                </a>
                <a href="#how" className="inline-flex items-center gap-2 bg-white border border-[#0B1E3F]/15 hover:border-[#0B1E3F]/40 text-[#0B1E3F] text-[14px] font-semibold px-6 py-3.5 rounded-full transition" data-testid="hero-cta-secondary">
                  {t('hero.ctaSecondary')}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[28px] overflow-hidden shadow-[0_30px_80px_-25px_rgba(11,30,63,0.35)] aspect-[4/5]">
                <img src={IMG_HERO} alt="Zagreb high-rise" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1E3F]/60 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#0B1E3F] flex items-center justify-center"><Building2 size={16} className="text-[#E9B949]" /></div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#0B1E3F]/50">Zagreb · Trnje</div>
                    <div className="text-[13px] font-semibold text-[#0B1E3F]">Radnički Torus</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-16 md:mt-20 grid grid-cols-3 gap-4 md:gap-8 border-t border-[#0B1E3F]/10 pt-10">
            {[
              [t('hero.stat1Value'), t('hero.stat1Label')],
              [t('hero.stat2Value'), t('hero.stat2Label')],
              [t('hero.stat3Value'), t('hero.stat3Label')],
            ].map(([v, k]) => (
              <div key={k}>
                <div className="font-serif text-[36px] md:text-[52px] leading-none text-[#0B1E3F] tracking-tight">{v}</div>
                <div className="mt-2 text-[11.5px] md:text-[12.5px] uppercase tracking-wider text-[#0B1E3F]/60">{k}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section id="about" className="scroll-mt-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] rounded-[24px] overflow-hidden">
                <img src={IMG_SEC1} alt="Croatia residential building" className="w-full h-full object-cover" />
              </div>
              <div className="mt-4 aspect-[16/10] rounded-[24px] overflow-hidden hidden md:block">
                <img src={IMG_SEC2} alt="Modern architecture" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#0B1E3F]/50">{t('intro.eyebrow')}</div>
              <h2 className="mt-3 font-serif text-[32px] md:text-[46px] leading-[1.1] font-semibold text-[#0B1E3F] tracking-[-0.01em]">{t('intro.title')}</h2>
              <div className="mt-6 space-y-5 text-[15.5px] leading-[1.75] text-[#0B1E3F]/75">
                <p>{t('intro.p1')}</p>
                <p>{t('intro.p2')}</p>
                <p>{t('intro.p3')}</p>
                <p>{t('intro.p4')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="scroll-mt-24 bg-[#0B1E3F] text-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E9B949]">{t('how.eyebrow')}</div>
              <h2 className="mt-3 font-serif text-[32px] md:text-[46px] leading-[1.1] font-semibold tracking-[-0.01em]">{t('how.title')}</h2>
            </div>
            <p className="max-w-md text-[15px] leading-relaxed text-white/70">{t('how.lead')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { i: <LayoutGrid size={20} />, t: 'how.step1Title', d: 'how.step1Desc', n: '01' },
              { i: <LineChart size={20} />, t: 'how.step2Title', d: 'how.step2Desc', n: '02' },
              { i: <Sparkles size={20} />, t: 'how.step3Title', d: 'how.step3Desc', n: '03' },
              { i: <Building2 size={20} />, t: 'how.step4Title', d: 'how.step4Desc', n: '04' },
            ].map((s) => (
              <div key={s.n} className="relative bg-white/[0.04] hover:bg-white/[0.08] transition rounded-2xl p-6 border border-white/10">
                <div className="absolute top-6 right-6 font-serif text-[13px] text-[#E9B949]/70">{s.n}</div>
                <div className="w-11 h-11 rounded-full bg-[#E9B949]/15 border border-[#E9B949]/30 text-[#E9B949] flex items-center justify-center">{s.i}</div>
                <div className="mt-5 font-serif text-[21px] font-semibold">{t(s.t)}</div>
                <div className="mt-2 text-[13.5px] leading-relaxed text-white/65">{t(s.d)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="scroll-mt-24">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#0B1E3F]/50">{t('trans.eyebrow')}</div>
            <h2 className="mt-3 font-serif text-[34px] md:text-[52px] leading-[1.08] font-semibold text-[#0B1E3F] tracking-[-0.01em] max-w-3xl mx-auto">{t('trans.title')}</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-6 text-[15.5px] leading-[1.75] text-[#0B1E3F]/75">
            <p>{t('trans.p1')}</p>
            <p><strong className="text-[#0B1E3F]">{t('trans.p2Bold')}</strong> {t('trans.p2')}</p>
            <p>{t('trans.p3')}</p>
            <p>{t('trans.p4')}</p>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="scroll-mt-24 bg-[#EFEAE1]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#0B1E3F]/50">{t('projects.eyebrow')}</div>
              <h2 className="mt-3 font-serif text-[32px] md:text-[46px] leading-[1.1] font-semibold text-[#0B1E3F] tracking-[-0.01em] max-w-2xl">{t('projects.title')}</h2>
            </div>
            <p className="max-w-md text-[15px] leading-relaxed text-[#0B1E3F]/70">{t('projects.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { img: IMG_PROJ1, name: t('projects.p1Name'), city: t('projects.p1City'), desc: t('projects.p1Desc') },
              { img: IMG_PROJ2, name: t('projects.p2Name'), city: t('projects.p2City'), desc: t('projects.p2Desc') },
              { img: IMG_PROJ3, name: t('projects.p3Name'), city: t('projects.p3City'), desc: t('projects.p3Desc') },
            ].map((p, i) => (
              <a href="#contact" key={i} className="group bg-white rounded-3xl overflow-hidden border border-[#0B1E3F]/10 hover:shadow-[0_30px_60px_-30px_rgba(11,30,63,0.35)] transition-all" data-testid={`project-card-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10.5px] uppercase tracking-wider text-[#0B1E3F]/50">{p.city}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#E9B949] bg-[#E9B949]/10 border border-[#E9B949]/30 rounded-full px-2 py-0.5">{t('projects.tag')}</span>
                  </div>
                  <h3 className="mt-2 font-serif text-[22px] font-semibold text-[#0B1E3F]">{p.name}</h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[#0B1E3F]/70">{p.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0B1E3F] group-hover:gap-2.5 transition-all">
                    {t('projects.viewAll')} <ArrowUpRight size={14} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="scroll-mt-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#0B1E3F]/50">{t('contact.eyebrow')}</div>
              <h2 className="mt-3 font-serif text-[32px] md:text-[46px] leading-[1.1] font-semibold text-[#0B1E3F] tracking-[-0.01em]">{t('contact.title')}</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-[#0B1E3F]/70 max-w-md">{t('contact.subtitle')}</p>
              <div className="mt-8 space-y-4">
                <ContactCard icon={<Mail size={16} />} title={t('contact.emailUs')} value={t('footer.emailValue')} href={`mailto:${t('footer.emailValue')}`} testId="contact-info-email" />
                <ContactCard icon={<Phone size={16} />} title={t('contact.callUs')} value={t('footer.phoneValue')} href={`tel:${t('footer.phoneValue').replace(/\s/g,'')}`} testId="contact-info-phone" />
                <ContactCard icon={<MapPin size={16} />} title={t('contact.visitUs')} value={t('footer.addressValue')} href="https://maps.google.com/?q=Ulica+grada+Vukovara+269D+Zagreb" testId="contact-info-address" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ContactCard({ icon, title, value, href, testId }) {
  return (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-[#0B1E3F]/10 hover:border-[#0B1E3F]/30 transition" data-testid={testId}>
      <div className="w-10 h-10 rounded-full bg-[#F6F3EE] flex items-center justify-center text-[#0B1E3F]">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#0B1E3F]/50">{title}</div>
        <div className="text-[14px] font-semibold text-[#0B1E3F] mt-0.5">{value}</div>
      </div>
    </a>
  );
}
